import type { TRefreshResponse, TSSOUser } from "@/types/sso"
import type { CookieOptions } from "#app"

export default defineNuxtPlugin(async (nuxtApp) => {
  const { sso: config, websiteUrl, baseUrl } = useRuntimeConfig().public
  const route = useRoute()

  const accessToken = useCookie(config.tokenName, {
    domain: config.domain,
    httpOnly: false,
    watch: true,
    maxAge: config.accessTokenTTL,
    secure: !import.meta.dev,
  })

  const refreshToken = useCookie(config.refreshTokenName, {
    domain: config.domain,
    httpOnly: false,
    watch: true,
    maxAge: config.refreshTokenTTL,
    secure: !import.meta.dev,
  })

  const user = useState<null | TSSOUser>("auth:user", () => null)

  const isTokenExpired = (token: string): boolean => {
    try {
      // JWT uses base64url (- and _ instead of + and /); normalize before decoding
      const base64Payload = (token.split('.')[1] as string).replace(/-/g, '+').replace(/_/g, '/')
      const payload = JSON.parse(atob(base64Payload)) as { exp: number }
      // 10-second buffer to account for clock skew and network latency
      return payload.exp * 1000 < Date.now() + 10_000
    } catch {
      return true
    }
  }

  // Shared promise to prevent concurrent refresh calls
  let refreshPromise: Promise<string> | null = null

  const doRefresh = async (): Promise<string> => {
    if (accessToken.value && !isTokenExpired(accessToken.value)) {
      return accessToken.value
    }
 
    const response = await $fetch<TRefreshResponse>('/api/sso/refresh', {
      method: 'POST',
      body: {
        refreshToken: refreshToken.value
      }
    })
 
    await setTokens({ ...response })
    return response.accessToken
  }

  const handle401Error = async (): Promise<string> => {
    if (refreshPromise) return refreshPromise
 
    refreshPromise = (
      typeof navigator !== 'undefined' && 'locks' in navigator
        ? navigator.locks.request('sso-token-refresh', () => doRefresh())
        : doRefresh() // fallback for environments without Web Locks support
    ).finally(() => {
      refreshPromise = null
    })
 
    return refreshPromise
  }

  const api = $fetch.create({
    baseURL: '/api',
    retry: 1,
    retryStatusCodes: [401],
    headers: {
      'x-app-domain': config.domain,
    },
    onRequest: async ({ options }) => {
      let token

      if (accessToken.value) {
        if (isTokenExpired(accessToken.value) && refreshToken.value) {
          try {
            token = await handle401Error()
          } catch {
            // Refresh failed — send request without token; server will reject if needed
          }
        } else {
          token = accessToken.value
        }
      } else if (!accessToken.value && refreshToken.value) {
        try {
          token = await handle401Error()
        } catch {
        }
      }

      if (token) options.headers.set('Authorization', `Bearer ${token}`)
    },
    onResponseError: async ({ response, options, error }) => {
      if (response.status === 401) {
        const ctx = tryUseNuxtApp()

        try {
          if (refreshToken.value) {
            await handle401Error()
          }
        } catch (e: any) {
          if (e.response && e.response.status === 401) {
            setTokens({ accessToken: null, refreshToken: null, expiresIn: 0 })
            
            if (ctx) await redirectTo()
            else await nuxtApp.runWithContext(async () => await redirectTo())
          }
        }
      }
    },
  })

  const setTokens = async (ctx: { accessToken: string | null, refreshToken: string | null, expiresIn: number }) => {
    accessToken.value = ctx.accessToken
    refreshToken.value = ctx.refreshToken
  }

  const logout = async (): Promise<boolean> => {
    await api('/sso/logout', {
      method: 'POST',
      body: {
        refreshToken: refreshToken.value
      }
    })

    nuxtApp.callHook('auth:logout')

    user.value = null
    accessToken.value = null
    refreshToken.value = null

    const tokenCookie = useCookie(config.tokenName)
    tokenCookie.value = null

    const refreshTokenCookie = useCookie(config.refreshTokenName)
    refreshTokenCookie.value = null

    return true
  }

  const fetchProfile = async (): Promise<TSSOUser> => {
    try {
      const response = await api<TSSOUser>('/sso/profile', {
        method: 'GET'
      })

      user.value = response

      return response
    } catch (e) {
      user.value = null

      throw e
    }
  }

  const redirectTo = () => {
    return navigateTo(`${websiteUrl}/login?redirectTo=${encodeURI(baseUrl + route.fullPath)}`, { external: true, redirectCode: 302, replace: true })
  }

  try {
    await fetchProfile()
  } catch (e) {}


  return {
    provide: {
      api,
      auth: {
        user: user.value,
        isLoggedIn: () => !!user.value,
        accessToken: () => accessToken.value,
        setTokens,
        handle401Error,
        fetchProfile,
        logout,
        redirectTo
      }
    }
  }
})
